import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  of,
  OperatorFunction,
  Subject,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import { FormActionTypes, NonGroup, SubmissionError, SubmissionResult, SubmissionTypes, TypedControlsIn, TypedFormGroup } from '../domain';
import { FormHelpers } from '../util-helpers/helpers';

export class TypedSubmissionForm<FormType, ResultType> {
  // Successful form submissions
  public onSuccess$: Observable<ResultType>;
  // Submission errors
  public onError$: Observable<string | null>;
  // Form was invalid when submitting
  public onInvalid$: Observable<unknown>;
  // is the form currently submitting
  public submitting$: Observable<boolean>;

  // Access the controls on the form without going through the group
  public controls: FormType extends NonGroup ? never : TypedControlsIn<FormType, never, never>;

  private actions: Subject<FormActionTypes> = new Subject<FormActionTypes>();
  private helpers = new FormHelpers();

  public constructor(
    public readonly group: TypedFormGroup<FormType>,
    submitOperator: OperatorFunction<FormType, ResultType>,
    initialValue: Partial<FormType> | Observable<Partial<FormType>>,
  ) {
    this.controls = group.controls;
    this.helpers.setInitialValue(initialValue, group);
    this.setStatuses(group, submitOperator);
  }

  public validate(): void {
    this.helpers.validateAllFormFields(this.group);
  }

  public validateAndSubmit(): void {
    this.actions.next(FormActionTypes.ValidateAndSubmit);
  }

  private setStatuses(group: TypedFormGroup<FormType>, submitOperator: OperatorFunction<FormType, ResultType>): void {
    const { valid$, nonValid$ } = this.helpers.getFormSubmitTypeStreams(this.actions.asObservable(), group);

    this.onInvalid$ = nonValid$;

    const submissions$ = this.createFormSubmissionStream(valid$, submitOperator, group);

    this.onSuccess$ = submissions$.pipe(
      filter((res: SubmissionResult<ResultType>) => res.type === SubmissionTypes.Success),
      map(({ payload }) => payload),
    );

    this.onError$ = merge(
      submissions$.pipe(
        filter((res: SubmissionResult<ResultType>) => res.type === SubmissionTypes.Error),
        map(({ payload }) => payload.message ?? payload),
      ),
      group.valueChanges.pipe(map(() => null)),
    ).pipe(distinctUntilChanged());

    this.submitting$ = merge(
      valid$.pipe(
        withLatestFrom(this.onError$),
        map(([valid, error]) => (error ? false : valid)),
      ),
      submissions$.pipe(map(() => false)),
    );
  }

  private createFormSubmissionStream(
    valid$: Observable<boolean>,
    operator: OperatorFunction<FormType, ResultType>,
    group: TypedFormGroup<FormType>,
  ) {
    return valid$.pipe(
      withLatestFrom(group.valueChanges, (_, value) => value),
      // The switchmap is to contain the catchError, otherwise the observable completes
      switchMap((value) =>
        of(value).pipe(
          operator,
          map((response) => ({
            type: SubmissionTypes.Success,
            payload: response,
          })),
          catchError((error: SubmissionError) => of({ type: SubmissionTypes.Error, payload: error })),
        ),
      ),
      rmaShareReplay(),
    );
  }
}
